import { Button, DotsMenu, Input, Modal } from "@common/components";
import { patchUser } from "@requests/user/user";
import { Service } from "@server/entities/user";
import React, { useState } from "react";
import { BsPlusCircleFill } from "react-icons/bs";
import { Edit, Trash } from "tabler-icons-react";

import { useProfile } from "../pages/Profile";

type ServiceDraft = { id?: string; name: string; price: string };

export const ProfileServices: React.FC = () => {
  const { user, isMe, mutate } = useProfile();
  const [editing, setEditing] = useState<ServiceDraft | null>(null);
  const [saving, setSaving] = useState(false);

  const services = user?.services ?? [];

  const persist = async (nextServices: ServiceDraft[]) => {
    if (!user) return;
    setSaving(true);
    await patchUser(user.id, { services: nextServices as Service[] });
    await mutate();
    setSaving(false);
  };

  const handleSubmit = async (draft: ServiceDraft) => {
    const next: ServiceDraft[] = draft.id
      ? services.map((s) =>
          s.id === draft.id
            ? { id: s.id, name: draft.name, price: draft.price }
            : { id: s.id, name: s.name, price: s.price },
        )
      : [
          ...services.map((s) => ({ id: s.id, name: s.name, price: s.price })),
          { name: draft.name, price: draft.price },
        ];
    await persist(next);
    setEditing(null);
  };

  const handleRemove = async (service: Service) => {
    await persist(
      services
        .filter((s) => s.id !== service.id)
        .map((s) => ({ id: s.id, name: s.name, price: s.price })),
    );
  };

  if (!isMe && services.length === 0) return <></>;

  return (
    <section id="services" className="py-10">
      <h2 className="mb-4">Services</h2>

      <div className="flex flex-wrap items-stretch gap-4">
        {services.map((service) => (
          <div
            key={service.id}
            className="relative w-52 p-6 rounded-xl bg-dark-800 border border-solid border-dark-900 transition hover:border-dark-800"
          >
            <h4>{service.name}</h4>
            <p>{service.price}</p>

            {isMe && (
              <DotsMenu
                className="absolute top-2 right-2"
                styles={{ item: { padding: "6px 10px" } }}
              >
                <DotsMenu.Dropdown>
                  <DotsMenu.Item
                    onClick={() => setEditing(service)}
                    icon={<Edit className="w-4" />}
                  >
                    Edit service
                  </DotsMenu.Item>

                  <DotsMenu.Item
                    onClick={() => handleRemove(service)}
                    icon={<Trash className="w-4" />}
                    color="red"
                  >
                    Delete service
                  </DotsMenu.Item>
                </DotsMenu.Dropdown>
              </DotsMenu>
            )}
          </div>
        ))}

        {isMe && (
          <div
            onClick={() => setEditing({ name: "", price: "" })}
            className="w-52 p-6 rounded-xl bg-dark-900 border border-solid border-dark-900 transition hover:border-dark-800 cursor-pointer flex items-center justify-center"
          >
            <BsPlusCircleFill className="opacity-70 w-12 h-12" />
          </div>
        )}
      </div>

      <Modal
        opened={!!editing}
        onClose={() => !saving && setEditing(null)}
        title={<h3>{editing?.id ? "Edit service" : "Add service"}</h3>}
        size="sm"
      >
        {editing && (
          <ServiceForm
            initial={editing}
            saving={saving}
            onSubmit={handleSubmit}
            onCancel={() => setEditing(null)}
          />
        )}
      </Modal>
    </section>
  );
};

interface ServiceFormProps {
  initial: ServiceDraft;
  saving: boolean;
  onSubmit: (draft: ServiceDraft) => void;
  onCancel: () => void;
}

const ServiceForm: React.FC<ServiceFormProps> = ({
  initial,
  saving,
  onSubmit,
  onCancel,
}) => {
  const [name, setName] = useState(initial.name);
  const [price, setPrice] = useState(initial.price);

  const canSubmit = name.trim().length > 0 && price.trim().length > 0;

  return (
    <div className="flex flex-col gap-3">
      <Input
        label="Service name"
        placeholder="e.g. Mixing"
        size="sm"
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
      />
      <Input
        label="Price"
        placeholder="e.g. $150 AUD or from $80"
        size="sm"
        value={price}
        onChange={(e) => setPrice(e.currentTarget.value)}
      />
      <div className="flex justify-end gap-2 mt-2">
        <Button variant="subtle" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={() =>
            canSubmit &&
            onSubmit({ id: initial.id, name: name.trim(), price: price.trim() })
          }
          loading={saving}
          disabled={!canSubmit}
        >
          Save
        </Button>
      </div>
    </div>
  );
};
